import {
  ButtonHTMLAttributes,
  AnchorHTMLAttributes,
  MouseEvent,
  ReactElement,
  Fragment,
  ElementType,
  ReactNode,
} from 'react';
import { Link, LinkProps } from 'react-router-dom';

import styled from 'styled-components';

import { IconProps } from '@components/icons/base';
import { Color, Font, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color' | 'disabled'> {}
interface AnchorProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'color' | 'disabled'> {}

export type ActionTextBaseProps = {
  color?: Color;
  hoverColor?: Color;
  font?: Font;
  ellipsis?: boolean;
  isDisabled?: boolean;
  underline?: boolean;
  center?: boolean;
  // Makes the click target the whole container rather than just the label.
  fill?: boolean;
  text?: string;
  iconLeft?: ReactElement<IconProps>;
  iconRight?: ReactElement<IconProps>;
};

interface ActionLinkProps extends ActionTextBaseProps, Omit<AnchorProps, 'href'> {
  to: LinkProps['to'];
}

export interface ActionTextProps extends ActionTextBaseProps, ButtonProps {
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
}

type ActionTextWrapperProps = {
  as: ElementType;
  font?: Font;
  color?: Color;
  hoverColor?: Color;
  ellipsis?: boolean;
  isDisabled?: boolean;
  underline?: boolean;
  center?: boolean;
  fill?: boolean;
  iconLeft?: ReactElement<IconProps>;
  iconRight?: ReactElement<IconProps>;
  text?: string;
  children?: ReactNode;
};

function ActionTextWrapper({
  as,
  font = 'regular16',
  color = 'elementBlue100',
  hoverColor = 'elementBlue100',
  ellipsis = false,
  isDisabled = false,
  underline = false,
  center = false,
  fill = false,
  iconLeft,
  iconRight,
  text,
  children,
  ...rest
}: ActionTextWrapperProps) {
  const hasIcon = Boolean(iconLeft || iconRight);
  const content = text ?? children;

  return (
    <StyledElement
      as={as}
      $font={font}
      $color={color}
      $hoverColor={hoverColor}
      $ellipsis={ellipsis}
      $isDisabled={isDisabled}
      $underline={underline}
      $center={center}
      $fill={fill}
      $hasIcon={hasIcon}
      {...rest}
    >
      <ActionTextContent iconLeft={iconLeft} iconRight={iconRight} content={content} />
    </StyledElement>
  );
}

export function ActionText(props: ActionTextProps) {
  return <ActionTextWrapper as="button" type="button" {...props} />;
}

function ActionTextLink(props: ActionLinkProps) {
  const { isDisabled, ...rest } = props;

  return <ActionTextWrapper as={isDisabled ? 'span' : Link} isDisabled={isDisabled} {...rest} />;
}

export interface ActionLinkExternalProps extends ActionTextBaseProps, AnchorProps {
  href: string;
}

function ActionTextLinkExternal(props: ActionLinkExternalProps) {
  const { isDisabled, ...rest } = props;

  return (
    <ActionTextWrapper
      as={isDisabled ? 'span' : 'a'}
      isDisabled={isDisabled}
      target="_blank"
      rel="noopener noreferrer"
      {...rest}
    />
  );
}

ActionText.Link = ActionTextLink;
ActionText.ExternalLink = ActionTextLinkExternal;

function ActionTextContent({
  iconLeft,
  iconRight,
  content,
}: {
  iconLeft?: ReactElement<IconProps>;
  iconRight?: ReactElement<IconProps>;
  content: React.ReactNode;
}) {
  const hasIcon = Boolean(iconLeft || iconRight);

  if (hasIcon) {
    return (
      <Fragment>
        {iconLeft}
        {content && <TextWrapper>{content}</TextWrapper>}
        {iconRight}
      </Fragment>
    );
  }
  return <>{content}</>;
}

const TextWrapper = styled.span({
  margin: `0 ${pxToRem(8)}`,
});

const StyledElement = styled.a<{
  $font: Font;
  $color: Color;
  $hoverColor: Color;
  $ellipsis: boolean;
  $isDisabled?: boolean;
  $underline?: boolean;
  $center?: boolean;
  $fill?: boolean;
  $hasIcon?: boolean;
}>((p) => ({
  background: 'transparent',
  padding: 0,
  margin: 0,
  border: 'none',
  transition: transitionStandard('all'),
  display:
    p.$fill || (p.$ellipsis && p.$hasIcon)
      ? 'flex'
      : p.$hasIcon
      ? 'inline-flex'
      : p.$ellipsis
      ? 'block'
      : 'inline-block',
  alignItems: p.$fill || p.$hasIcon ? 'center' : undefined,
  gap: p.$fill || p.$hasIcon ? pxToRem(4) : undefined,
  ...(p.$fill && {
    alignSelf: 'stretch',
    flex: 1,
    minWidth: 0,
  }),
  outline: 'none',

  font: theme.font[p.$font],
  color: theme.color[p.$color],
  textAlign: p.$center ? 'center' : 'left',

  ...(!p.$isDisabled && {
    '&:not(:disabled)': {
      '&:hover, &:focus': {
        cursor: 'pointer',
        color: theme.color[p.$hoverColor],
        textDecoration: 'underline',
      },
      ...(p.$underline && { textDecoration: 'underline' }),
    },
  }),

  ...(p.$ellipsis && {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),

  svg: {
    display: 'block',
  },
}));
